/**
 * Registry of unsaved work (e.g. the annotator's pending edits, a save still running after
 * leaving the annotator). The app asks it before doing anything that would lose that work:
 * a 401 mid-session opens a re-login dialog instead of redirecting to `/login` while
 * {@link hasUnsavedWork} is true, and flushes everything once the user signed in again.
 */

export interface UnsavedWork {
  /** Short description for dialogs ("3 unsaved changes on K_PF_-6_b"). */
  label?: () => string;
  hasUnsaved: () => boolean;
  /** Tries to save now; resolves when that attempt settled. */
  flush: () => Promise<unknown>;
}

const entries = new Set<UnsavedWork>();
let leaving = false;

/** Registers `work`; returns the function that removes it again. */
export function registerUnsavedWork(work: UnsavedWork): () => void {
  entries.add(work);
  return () => {
    entries.delete(work);
  };
}

export function hasUnsavedWork(): boolean {
  for (const work of entries) {
    try {
      if (work.hasUnsaved()) return true;
    } catch {
      // a broken guard must not block sign-out
    }
  }
  return false;
}

/** Labels of the registered work that is still unsaved. */
export function unsavedWorkLabels(): string[] {
  const out: string[] = [];
  for (const work of entries) {
    if (work.hasUnsaved() && work.label) out.push(work.label());
  }
  return out;
}

/** Flushes every registered piece of work (errors are left to the owners to report). */
export async function flushUnsavedWork(): Promise<void> {
  await Promise.allSettled([...entries].filter((w) => w.hasUnsaved()).map((w) => w.flush()));
}

/**
 * The user chose to leave and drop what is unsaved (e.g. "Sign out" in the re-login
 * dialog): `beforeunload` guards stop prompting.
 */
export function markLeavingIntentionally(): void {
  leaving = true;
}

export function isLeavingIntentionally(): boolean {
  return leaving;
}

/** Tests. */
export function resetUnsavedWork(): void {
  entries.clear();
  leaving = false;
}
